import React from 'react';
import { Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import type { OrderItem } from '@/shared/api/types';
import { formatCurrency } from '../../query/utils/orderUtils';

const { Text } = Typography;

export interface OrderItemsTableProps {
  /** 订单商品列表 */
  items: OrderItem[];
  /** 是否显示操作列 */
  showActions?: boolean;
  /** 是否显示汇总 */
  showSummary?: boolean;
  /** 是否可编辑 */
  editable?: boolean;
  /** 是否正在加载 */
  loading?: boolean;
  /** 表格样式 */
  style?: React.CSSProperties;
  /** 表格类名 */
  className?: string;
}

const OrderItemsTable: React.FC<OrderItemsTableProps> = ({
  items,
  showActions = false,
  showSummary = true,
  editable = false,
  loading = false,
  style,
  className
}) => {
  const { t } = useTranslation(['modules', 'common']);

  // 计算总金额
  const totalAmount = items.reduce((sum, item) => {
    return sum + (item.unit_price || 0) * (item.quantity || 1);
  }, 0);

  // 计算商品总数
  const totalQuantity = items.reduce((sum, item) => {
    return sum + (item.quantity || 1);
  }, 0);

  // 表格列配置
  const columns: ColumnsType<OrderItem> = [
    {
      title: t('modules:order.detail.items.productId', '商品ID'),
      dataIndex: 'product_id',
      key: 'product_id',
      width: 120,
      render: (productId: string) => (
        <Text code>{productId}</Text>
      )
    },
    {
      title: t('modules:order.detail.items.productName', '商品名称'),
      key: 'product_name',
      width: 200,
      render: (_, record: any) => (
        <Text strong>
          {record.product_name || `商品 ${record.product_id.slice(0, 8)}`}
        </Text>
      )
    },
    {
      title: t('modules:order.detail.items.quantity', '数量'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 80,
      align: 'center' as const,
      render: (quantity: number) => (
        <Tag color="blue">{quantity}</Tag>
      )
    },
    {
      title: t('modules:order.detail.items.unitPrice', '单价'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 120,
      align: 'right' as const,
      render: (price: number, record: OrderItem) => (
        <Text>{formatCurrency(price, record.currency)}</Text>
      )
    },
    {
      title: t('modules:order.detail.items.subtotal', '小计'),
      key: 'subtotal',
      width: 120,
      align: 'right' as const,
      render: (_, record: OrderItem) => {
        const subtotal = (record.unit_price || 0) * (record.quantity || 1);
        return (
          <Text strong>{formatCurrency(subtotal, record.currency)}</Text>
        );
      }
    },
    {
      title: t('modules:order.detail.items.currency', '货币'),
      dataIndex: 'currency',
      key: 'currency',
      width: 80,
      align: 'center' as const,
      render: (currency: string) => (
        <Tag>{currency}</Tag>
      )
    }
  ];

  // 添加操作列（如果需要）
  if (showActions && editable) {
    columns.push({
      title: t('modules:order.detail.items.actions', '操作'),
      key: 'actions',
      width: 100,
      align: 'center' as const,
      render: () => (
        // 这里可以添加编辑、删除等操作按钮
        <span style={{ color: '#1890ff', cursor: 'pointer' }}>
          {t('modules:order.detail.items.edit', '编辑')}
        </span>
      )
    });
  }

  // 表格数据
  const tableData = items.map((item, index) => ({
    ...item,
    key: item.product_id || `item-${index}`
  }));

  // 表格汇总行
  const summary = () => {
    if (!showSummary) return null;

    return (
      <Table.Summary fixed>
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={2}>
            <Text strong>
              {t('modules:order.detail.items.total', '合计')}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="center">
            <Text strong>{totalQuantity}</Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={3} align="right">
            <Text type="secondary">
              {t('modules:order.detail.items.avgPrice', '平均')}: {items.length > 0 ? formatCurrency(totalAmount / totalQuantity, items[0]?.currency || 'USD') : '¥0.00'}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={4} align="right">
            <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
              {formatCurrency(totalAmount, items[0]?.currency || 'USD')}
            </Text>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={5} align="center">
            <Text strong>{items[0]?.currency || 'USD'}</Text>
          </Table.Summary.Cell>
          {showActions && editable && (
            <Table.Summary.Cell index={6} />
          )}
        </Table.Summary.Row>
      </Table.Summary>
    );
  };

  return (
    <div className="order-items-table">
      <Table<OrderItem>
        columns={columns}
        dataSource={tableData}
        loading={loading}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
        bordered
        summary={summary}
        style={style}
        className={className}
      />

      {/* 统计信息 */}
      {showSummary && (
        <div style={{ marginTop: 16, padding: '12px 16px', backgroundColor: '#fafafa', borderRadius: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <Text type="secondary">
                {t('modules:order.detail.items.summary', '订单商品统计')}:
              </Text>
            </div>
            <div>
              <Text>
                {t('modules:order.detail.items.totalItems', '商品种类')}:{' '}
                <Text strong>{items.length}</Text>
              </Text>
              <Text style={{ marginLeft: 16 }}>
                {t('modules:order.detail.items.totalQuantity', '商品总数')}:{' '}
                <Text strong>{totalQuantity}</Text>
              </Text>
              <Text style={{ marginLeft: 16 }}>
                {t('modules:order.detail.items.totalAmount', '订单总额')}:{' '}
                <Text strong style={{ color: '#1890ff' }}>
                  {formatCurrency(totalAmount, items[0]?.currency || 'USD')}
                </Text>
              </Text>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderItemsTable;